import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface Plan {
  id: string;
  name: string;
  description: string;
  priceMonthly: number | null;
  priceYearly: number | null;
  priceLifetime: number | null;
  features: string[];
  limits: {
    chatMessages?: number;
    codebases?: number;
    mcpServers?: number;
    storage?: number;
  };
}

interface Subscription {
  planId: string;
  planName: string;
  planExpiresAt: string | null;
  isSubscribed: boolean;
}

interface Usage {
  usage: {
    chatMessages: number;
    codebases: number;
    mcpServers: number;
    storageUsed: number;
  };
  limits: {
    chatMessages?: number;
    codebases?: number;
    mcpServers?: number;
    storage?: number;
  };
  percentages: {
    chatMessages: number;
    codebases: number;
    mcpServers: number;
    storage: number;
  };
  isWithinLimit: boolean;
}

export default function BillingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [showRedeem, setShowRedeem] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [redeemError, setRedeemError] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/status'),
      ]);

      if (plansRes.data.success) {
        setPlans(plansRes.data.data);
      }
      if (statusRes.data.success) {
        setSubscription(statusRes.data.data.subscription);
        setUsage(statusRes.data.data.usage);
      }
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string, planType: string) => {
    setProcessing(priceId);
    try {
      const response = await api.post('/billing/checkout', { priceId, planType });
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        alert('Failed to start checkout: ' + response.data.error);
      }
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleManageSubscription = async () => {
    setProcessing('portal');
    try {
      const response = await api.post('/billing/portal');
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        alert('Failed to open customer portal');
      }
    } catch (error: any) {
      alert('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessing(null);
    }
  };

  const handleRedeemCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setRedeemError('');
    setRedeemSuccess('');

    try {
      const response = await api.post('/billing/redeem', { code: inviteCode });
      if (response.data.success) {
        setRedeemSuccess(`Successfully redeemed! You now have the ${response.data.planName} plan.`);
        setInviteCode('');
        loadData();
      } else {
        setRedeemError(response.data.error || 'Failed to redeem code');
      }
    } catch (error: any) {
      setRedeemError(error.response?.data?.error || 'Invalid code');
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Billing & Subscription</h1>

      {/* Current Plan */}
      {subscription && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Current Plan</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold">{subscription.planName}</p>
              {subscription.planExpiresAt && (
                <p className="text-gray-500">
                  {subscription.isSubscribed 
                    ? `Renews on ${new Date(subscription.planExpiresAt).toLocaleDateString()}`
                    : `Expires on ${new Date(subscription.planExpiresAt).toLocaleDateString()}`
                  }
                </p>
              )}
            </div>
            {subscription.isSubscribed && (
              <button
                onClick={handleManageSubscription}
                disabled={processing === 'portal'}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                {processing === 'portal' ? 'Loading...' : 'Manage Subscription'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Usage */}
      {usage && (
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Usage This Month</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Chat Messages */}
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Chat Messages</span>
                <span className="font-medium">
                  {usage.usage.chatMessages} / {usage.limits.chatMessages || '∞'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    usage.percentages.chatMessages > 90 ? 'bg-red-500' : 'bg-blue-600'
                  }`}
                  style={{ width: `${Math.min(usage.percentages.chatMessages, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Codebases */}
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Codebases</span>
                <span className="font-medium">
                  {usage.usage.codebases} / {usage.limits.codebases || '∞'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    usage.percentages.codebases > 90 ? 'bg-red-500' : 'bg-green-600'
                  }`}
                  style={{ width: `${Math.min(usage.percentages.codebases, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* MCP Servers */}
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">MCP Servers</span>
                <span className="font-medium">
                  {usage.usage.mcpServers} / {usage.limits.mcpServers || '∞'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    usage.percentages.mcpServers > 90 ? 'bg-red-500' : 'bg-purple-600'
                  }`}
                  style={{ width: `${Math.min(usage.percentages.mcpServers, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Storage */}
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Storage</span>
                <span className="font-medium">
                  {formatBytes(usage.usage.storageUsed)} / {usage.limits.storage ? `${usage.limits.storage} MB` : '∞'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    usage.percentages.storage > 90 ? 'bg-red-500' : 'bg-yellow-600'
                  }`}
                  style={{ width: `${Math.min(usage.percentages.storage, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Code Redemption */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <button
          onClick={() => setShowRedeem(!showRedeem)}
          className="text-blue-600 font-medium hover:underline"
        >
          {showRedeem ? 'Hide' : 'Have an invitation code?'}
        </button>

        {showRedeem && (
          <form onSubmit={handleRedeemCode} className="mt-4">
            <div className="flex gap-4">
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Enter invitation code (e.g., HAL-XXX-XXXXXXXX)"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Redeem
              </button>
            </div>
            {redeemError && <p className="mt-2 text-red-500">{redeemError}</p>}
            {redeemSuccess && <p className="mt-2 text-green-500">{redeemSuccess}</p>}
          </form>
        )}
      </div>

      {/* Plans */}
      <h2 className="text-2xl font-bold mb-6">Choose Your Plan</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => {
          const isCurrentPlan = subscription?.planId === plan.id;
          
          return (
            <div
              key={plan.id}
              className={`bg-white rounded-lg shadow p-6 ${
                isCurrentPlan ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
              <p className="text-gray-500 mb-4">{plan.description}</p>

              <div className="mb-6">
                {plan.priceMonthly && (
                  <p className="text-3xl font-bold">
                    ${plan.priceMonthly}
                    <span className="text-base font-normal text-gray-500">/month</span>
                  </p>
                )}
                {plan.priceYearly && (
                  <p className="text-lg text-gray-500">
                    ${plan.priceYearly}/year (save {Math.round((1 - plan.priceYearly / (plan.priceMonthly! * 12)) * 100)}%)
                  </p>
                )}
                {plan.priceLifetime && (
                  <p className="text-2xl font-bold text-purple-600">
                    ${plan.priceLifetime}
                    <span className="text-base font-normal text-gray-500"> lifetime</span>
                  </p>
                )}
                {!plan.priceMonthly && !plan.priceYearly && !plan.priceLifetime && (
                  <p className="text-2xl font-bold">Free</p>
                )}
              </div>

              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
                {plan.limits.chatMessages && (
                  <li className="text-sm text-gray-500">
                    • {plan.limits.chatMessages} chat messages/month
                  </li>
                )}
                {plan.limits.codebases && (
                  <li className="text-sm text-gray-500">
                    • {plan.limits.codebases} codebases
                  </li>
                )}
                {plan.limits.mcpServers && (
                  <li className="text-sm text-gray-500">
                    • {plan.limits.mcpServers} MCP servers
                  </li>
                )}
              </ul>

              {isCurrentPlan ? (
                <button
                  disabled
                  className="w-full py-3 bg-green-100 text-green-700 font-semibold rounded-lg"
                >
                  Current Plan
                </button>
              ) : plan.priceMonthly || plan.priceYearly || plan.priceLifetime ? (
                <div className="space-y-2">
                  {plan.priceMonthly && (
                    <button
                      onClick={() => handleSubscribe(plan.id, 'monthly')}
                      disabled={!!processing}
                      className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {processing === plan.id ? 'Processing...' : 'Subscribe Monthly'}
                    </button>
                  )}
                  {plan.priceYearly && (
                    <button
                      onClick={() => handleSubscribe(plan.id, 'yearly')}
                      disabled={!!processing}
                      className="w-full py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {processing === plan.id ? 'Processing...' : 'Subscribe Yearly'}
                    </button>
                  )}
                  {plan.priceLifetime && (
                    <button
                      onClick={() => handleSubscribe(plan.id, 'lifetime')}
                      disabled={!!processing}
                      className="w-full py-3 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-900 disabled:opacity-50"
                    >
                      {processing === plan.id ? 'Processing...' : 'Get Lifetime'}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  disabled
                  className="w-full py-3 bg-gray-100 text-gray-500 font-semibold rounded-lg"
                >
                  Free Plan
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
